/**
 * Bet Ticker CM member multi-select — chips + typeahead via searchHierarchyNew.
 * Visual shell matches w7-input (class `w7-bm-member-search`).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon, Spinner } from '@w7/ui'
import { hasLiveApi } from '@/api/client'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'

const DEBOUNCE_MS = 100

export type TickerMemberSearchProps = {
  selected: string[]
  draft: string
  onDraftChange: (next: string) => void
  onAdd: (name: string) => void
  onRemove: (name: string) => void
  onClear: () => void
  onEnter: () => void
  fixtureSuggestions: string[]
}

export function TickerMemberSearch({
  selected,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  onClear,
  onEnter,
  fixtureSuggestions,
}: TickerMemberSearchProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [debouncedDraft, setDebouncedDraft] = useState('')
  const live = hasLiveApi()

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedDraft(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const hierarchySearch = useSearchHierarchyNew(debouncedDraft, open, {
    prefetchOnEmpty: true,
  })

  const suggestions = useMemo(() => {
    const exclude = (names: string[]) => names.filter((name) => !selected.includes(name))
    if (live) return exclude(hierarchySearch.data ?? [])
    const needle = draft.trim().toLowerCase()
    return exclude(
      fixtureSuggestions.filter((name) => !needle || name.toLowerCase().includes(needle)),
    )
  }, [live, hierarchySearch.data, draft, fixtureSuggestions, selected])

  const showPanel =
    open &&
    (hierarchySearch.isFetching || suggestions.length > 0 || (live && hierarchySearch.isError))

  const showClear = Boolean(draft.trim() || selected.length > 0)

  const pick = (name: string) => {
    onAdd(name)
    onDraftChange('')
    setOpen(true)
  }

  return (
    <div className="w7-bm-member-search" ref={rootRef}>
      <div
        className="w7-bm-member-search-field"
        onFocusCapture={() => setOpen(true)}
      >
        {selected.map((name) => (
          <button
            key={name}
            type="button"
            className="w7-bm-member-search-chip"
            onClick={() => onRemove(name)}
            title={`Remove ${name}`}
          >
            {name} ×
          </button>
        ))}
        <input
          type="text"
          value={draft}
          placeholder="Enter or Select Member Name to Search"
          aria-label="Enter or Select Member Name to Search"
          aria-expanded={showPanel}
          aria-controls="w7-bm-member-suggest-list"
          aria-autocomplete="list"
          aria-busy={live && hierarchySearch.isFetching}
          onChange={(e) => {
            onDraftChange(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onEnter()
            }
            if (e.key === 'Escape') setOpen(false)
          }}
        />
        {showClear ? (
          <button
            type="button"
            className="w7-bm-member-search-clear"
            aria-label="Clear members"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onDraftChange('')
              onClear()
              setOpen(false)
            }}
          >
            <Icon name="close" size={14} />
          </button>
        ) : null}
      </div>

      {showPanel ? (
        <ul
          id="w7-bm-member-suggest-list"
          className="w7-bm-member-search-suggest"
          role="listbox"
          aria-label="Member suggestions"
        >
          {live && hierarchySearch.isFetching && suggestions.length === 0 ? (
            <li className="w7-bm-member-search-status" role="presentation">
              <Spinner size={14} />
              <span>Searching downline…</span>
            </li>
          ) : null}
          {live && hierarchySearch.isError ? (
            <li className="w7-bm-member-search-status" role="presentation">
              Could not load members.
            </li>
          ) : null}
          {suggestions.map((name) => (
            <li key={name} role="presentation">
              <button
                type="button"
                role="option"
                className="w7-bm-member-search-option"
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(name)
                }}
              >
                {name}
              </button>
            </li>
          ))}
          {!hierarchySearch.isFetching &&
          suggestions.length === 0 &&
          !hierarchySearch.isError ? (
            <li className="w7-bm-member-search-status" role="presentation">
              No members match “{draft.trim() || '…'}”.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
